import GROUPS from '@Front/enums/Groups';
import Api from './api';

class Storage {
  #stockIdsByGroup = new Map();

  constructor() {
    this.#init();
  }

  getGroupsStockIds(groupSlug) {
    const vehicles = this.#stockIdsByGroup.get(groupSlug);

    return Object.keys(vehicles);
  }

  getGroupsVehicles(groupSlug) {
    const vehicles = this.#stockIdsByGroup.get(groupSlug);

    // Filter out expired vehicles that are no longer in the DB
    Object.keys(vehicles).forEach((stockId) => {
      if (!vehicles[stockId].expired) return;

      delete vehicles[stockId];
    });

    return vehicles;
  }

  setVehicles(groupSlug, vehicles) {
    this.#stockIdsByGroup.set(groupSlug, vehicles);

    localStorage.setItem(groupSlug, JSON.stringify(vehicles));
  }

  add(groupSlug, vehicle) {
    const { stockId } = vehicle;
    const stockIdExists = this.hasVehicle(groupSlug, stockId);

    if (stockIdExists) return;

    const indexedVehicle = {};
    indexedVehicle[vehicle.stockId] = vehicle.toObject();

    const vehicles = this.getGroupsVehicles(groupSlug);

    const merged = {
      ...indexedVehicle,
      ...vehicles,
    };

    this.setVehicles(groupSlug, merged);
  }

  remove(groupSlug, stockId) {
    const vehicles = this.getGroupsVehicles(groupSlug);
    const exists = vehicles[stockId] !== undefined;

    if (!exists) return;

    delete vehicles[stockId];

    const allVehiclesRemoved = !this.groupHasVehicles(groupSlug);

    if (allVehiclesRemoved) {
      localStorage.removeItem(groupSlug);
      return;
    }

    this.setVehicles(groupSlug, vehicles);
  }

  selectedProductCount(groupSlug) {
    const vehicles = this.getGroupsVehicles(groupSlug);

    return Object.keys(vehicles).length;
  }

  clearGroup(groupSlug) {
    this.#stockIdsByGroup.set(groupSlug, {});
    localStorage.removeItem(groupSlug);
  }

  hasVehicle(groupSlug, stockId) {
    const vehicles = this.getGroupsVehicles(groupSlug);

    return vehicles[stockId] !== undefined;
  }

  groupHasVehicles(groupSlug) {
    const vehicles = this.getGroupsVehicles(groupSlug);

    return Object.keys(vehicles).length > 0;
  }

  async syncData(groupSlug) {
    const groupHasVehicles = this.groupHasVehicles(groupSlug);

    if (!groupHasVehicles) return;

    const selectedProducts = this.getGroupsVehicles(groupSlug);
    const stockIds = Object.keys(selectedProducts);
    const productData = await Api.getProductData(stockIds);

    Object.keys(selectedProducts).forEach((stockId) => {
      if (!productData[stockId]) {
        selectedProducts[stockId].expired = true;
        return;
      }

      selectedProducts[stockId] = productData[stockId];
    });

    this.setVehicles(groupSlug, selectedProducts);
  }

  removeExpiredProducts(groupSlug) {
    if (!this.groupHasVehicles(groupSlug)) return;

    const selectedVehicles = this.getGroupsVehicles(groupSlug);

    Object.keys(selectedVehicles).forEach((stockId) => {
      if (!selectedVehicles[stockId].expired) return;

      delete selectedVehicles[stockId];
    });

    this.setVehicles(groupSlug, selectedVehicles);
  }

  #seedMap() {
    Object.values(GROUPS).forEach((group) => {
      const content = localStorage.getItem(group);

      if (!content) {
        this.#stockIdsByGroup.set(group, {});
        return;
      }

      const parsed = JSON.parse(content);

      this.#stockIdsByGroup.set(group, parsed);
    });
  }

  #init() {
    this.#seedMap();
  }
}

const singleton = new Storage();

export default singleton;
